<template>
  <div class="agent-dashboard">
    <!-- OpenClaw 未安装引导页 -->
    <div v-if="openclawNotInstalled" class="openclaw-setup-guide">
      <div class="setup-icon">{{ mode === 'cloud' ? '☁️🦞' : '🦞' }}</div>
      <div class="setup-title">{{ mode === 'cloud' ? '云小龙虾' : '你尚未安装 OpenClaw' }}</div>
      <div class="setup-desc">{{ mode === 'cloud' ? '云端沙盒，开箱即用' : '在你的电脑上安装数字员工' }}</div>

      <div class="setup-cards">
        <!-- 本地安装卡片 -->
        <div v-if="mode === 'local'" class="setup-card">
          <div class="card-header">
            <span class="card-icon">💻</span>
            <div>
              <div class="card-title">本地安装</div>
              <div class="card-subtitle">安装到你的电脑上</div>
            </div>
          </div>
          <div class="card-badge free">免费</div>
          <div class="card-body">
            <div class="platform-tabs">
              <div class="platform-tab" :class="{ active: installTab === 'windows' }" @click="installTab = 'windows'">🪟 Windows</div>
              <div class="platform-tab" :class="{ active: installTab === 'mac' }" @click="installTab = 'mac'">🍎 macOS</div>
              <div class="platform-tab" :class="{ active: installTab === 'linux' }" @click="installTab = 'linux'">🐧 Linux</div>
            </div>
            <div class="install-content">
              <div class="install-hint install-hint-step1">
                <template v-if="installTab === 'windows'">❶ 按 <kbd>Win + X</kbd> 选择「Windows PowerShell」或搜索栏输入 <kbd>powershell</kbd></template>
                <template v-if="installTab === 'mac'">❶ 按 <kbd>⌘ Command + 空格</kbd> 打开聚焦搜索，输入 <kbd>Terminal</kbd> 回车</template>
                <template v-if="installTab === 'linux'">❶ 按 <kbd>Ctrl + Alt + T</kbd> 打开终端（大多数发行版通用）</template>
              </div>
              <div class="install-hint">❷ 粘贴以下命令并回车：</div>
              <div class="install-cmd" @click="copyCmd(installTab === 'windows' ? psCmd : shCmd)">
                <code>{{ installTab === 'windows' ? psCmd : shCmd }}</code>
                <i class="el-icon-document-copy copy-icon"></i>
              </div>
            </div>
          </div>
          <div class="card-footer">
            <el-button type="primary" size="small" icon="el-icon-refresh" @click="retryLoad" :loading="loading">安装完成，重新检测</el-button>
          </div>
        </div>

        <!-- 云电脑卡片 -->
        <div v-if="mode === 'cloud'" class="setup-card card-cloud">
          <div class="card-header">
            <span class="card-icon">☁️</span>
            <div>
              <div class="card-title">云电脑安装</div>
              <div class="card-subtitle">傻蛋AI官方出品</div>
              <div class="card-subtitle">云端沙盒，开箱即用</div>
            </div>
          </div>
          <div class="card-badge recommend">🔥 推荐</div>
          <div class="card-body">
            <ul class="cloud-advantages">
              <li>🔒 <b>安全隔离</b> — 云端沙盒环境，安全可靠</li>
              <li>🧠 <b>开箱即用</b> — 按行业预装上百种 AI 技能</li>
              <li>💰 <b>399元/年</b> — 服务器费用，免安装费</li>
            </ul>
          </div>
          <div class="card-footer">
            <el-button type="primary" size="small" @click="onCloudInstall" :loading="cloudPayLoading">立即安装</el-button>
            <el-button type="warning" size="small" @click="onCloudInstallTest" :loading="cloudPayLoading" style="margin-left:8px;">测试(1分)</el-button>
            <div class="cloud-contact">企业定制部署，请<a href="javascript:;" @click="onContactUs">联系我们</a></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 云电脑安装中 -->
    <div v-else-if="cloudInstalling" class="openclaw-setup-guide">
      <div class="cloud-loading-container">
        <div class="cloud-loading-icon">☁️🦞</div>
        <div class="cloud-loading-title">正在启动云电脑</div>
        <div class="cloud-loading-status">
          <i class="el-icon-loading"></i>
          <span>{{ cloudStatusMsg || '正在检测云端环境...' }}</span>
        </div>
        <div class="cloud-loading-hint">首次启动可能需要 1-2 分钟，请耐心等待</div>
      </div>
    </div>

    <!-- 正常面板（本地/云端已开通） -->
    <template v-else>
    <!-- 顶部 Hero 导航栏 -->
    <div class="dashboard-hero">
      <div class="hero-left">
        <div class="hero-icon">🦞</div>
        <div class="hero-text">
          <div class="hero-title">数字员工</div>
        </div>
        <div class="hero-tabs">
          <div class="tab" :class="{ active: viewMode === 'list' }" @click="viewMode = 'list'">
            <i class="el-icon-s-grid"></i> 员工列表
          </div>
          <div class="tab" :class="{ active: viewMode === 'org' }" @click="viewMode = 'org'">
            <i class="el-icon-office-building"></i> 组织架构
          </div>
          <div class="tab" :class="{ active: viewMode === 'gateway' }" @click="viewMode = 'gateway'">
            <i class="el-icon-monitor"></i> 管理网关
          </div>
          <el-select v-if="viewMode === 'gateway' && devices.length > 1" v-model="selectedDeviceIdx" size="mini" placeholder="选择设备" @change="onDeviceChange" class="device-select">
            <el-option v-for="(d, i) in devices" :key="d.nodeId" :label="d.nodeName || d.nodeId" :value="i">
              <span>{{ d.nodeName || d.nodeId }}</span>
              <span style="color:#9ca3af;margin-left:8px;font-size:11px">{{ d.customHost || d.wanIp || d.lanIp }}</span>
            </el-option>
          </el-select>
        </div>
      </div>
      <div class="hero-right">
        <el-button v-if="viewMode === 'list'" size="mini" class="create-btn" icon="el-icon-plus" @click="showCreateDialog = true">新建</el-button>
        <div class="hero-stats">
          <div class="stat-item">
            <div class="stat-value">{{ bots.length }}</div>
            <div class="stat-label">员工</div>
          </div>
          <div class="stat-item">
            <div class="stat-value stat-online">{{ onlineCount }}</div>
            <div class="stat-label">在线</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 员工网格 -->
    <div class="dashboard-body" v-if="viewMode === 'list'">
      <div class="bot-grid" v-loading="loading">
        <!-- 空状态 -->
        <div v-if="!loading && bots.length === 0" class="empty-state">
          <div class="empty-icon">🤖</div>
          <div class="empty-title">暂无数字员工</div>
          <div class="empty-desc">创建你的第一个 AI 数字员工，接入 OpenClaw 智能体</div>
          <el-button type="primary" size="small" icon="el-icon-plus" @click="showCreateDialog = true">新建员工</el-button>
        </div>

        <!-- Agent 卡片 -->
        <div
          v-for="bot in bots"
          :key="bot.id"
          class="agent-card"
          :class="{ 'agent-card--online': isBotOnline(bot) }"
          @click="openChat(bot)"
        >
          <!-- 状态指示点 -->
          <div class="card-status-dot" :class="isBotOnline(bot) ? 'online' : 'offline'"></div>

          <!-- 头像 -->
          <div class="card-avatar" :style="{ background: getAgentColor(bot) }">
            <img v-if="bot.headImage" :src="bot.headImage" class="card-avatar-img" />
            <span v-else class="card-avatar-emoji">{{ getAgentEmoji(bot) }}</span>
          </div>

          <!-- 信息 -->
          <div class="card-name">{{ bot.nickName }}</div>
          <div class="card-agent-id">{{ bot.openclawAgentId }}</div>

          <!-- 标签 -->
          <div class="card-tags">
            <span class="tag tag-bot">Bot</span>
            <span v-if="isBotOnline(bot)" class="tag tag-online">在线</span>
          </div>

          <!-- 操作按钮 -->
          <div class="card-actions" @click.stop>
            <button class="action-btn action-chat" @click="openChat(bot)" title="发消息">
              <i class="el-icon-chat-dot-round"></i>
            </button>
            <button class="action-btn action-edit" @click="openFiles(bot)" title="编辑人格">
              <i class="el-icon-edit-outline"></i>
            </button>
            <button class="action-btn action-delete" @click="onDelete(bot)" title="删除">
              <i class="el-icon-delete"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 网关 iframe -->
    <div class="dashboard-body gateway-body" v-if="viewMode === 'gateway'">
      <div v-if="probing" class="empty-state">
        <i class="el-icon-loading" style="font-size:32px;color:#6366f1;margin-bottom:12px"></i>
        <div class="empty-title">正在探测网关连通性…</div>
      </div>
      <div v-else-if="!gatewayUrl" class="empty-state">
        <div class="empty-icon">📡</div>
        <div class="empty-title">暂无在线设备</div>
        <div class="empty-desc">请先在设备上启动 OpenClaw 网关服务</div>
      </div>
      <iframe v-else :src="gatewayUrl" class="gateway-iframe" frameborder="0" allow="clipboard-read; clipboard-write"></iframe>
    </div>

    <!-- 组织架构拓扑图 -->
    <div class="dashboard-body org-body" v-if="viewMode === 'org'">
      <AgentOrgTopology :bots="allBots" />
    </div>

    <!-- 创建对话框 -->
    <el-dialog title="新建数字员工" :visible.sync="showCreateDialog" width="420px" append-to-body custom-class="create-dialog">
      <el-form :model="createForm" label-width="80px" size="small">
        <el-form-item label="昵称">
          <el-input v-model="createForm.nickName" placeholder="如: 客服小助手"></el-input>
        </el-form-item>
        <el-form-item label="头像URL">
          <el-input v-model="createForm.headImage" placeholder="可选，留空使用默认头像"></el-input>
        </el-form-item>
        <el-form-item label="模型">
          <el-select v-model="createForm.model" placeholder="选择模型" filterable :loading="modelsLoading">
            <el-option v-for="m in availableModels" :key="m" :label="m" :value="m"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="showCreateDialog = false">取消</el-button>
        <el-button size="small" type="primary" :loading="creating" @click="onCreate">创建</el-button>
      </span>
    </el-dialog>

    <!-- 文件编辑面板 -->
    <AgentFilesPanel
      v-if="filesPanel.visible"
      :gateway-client="filesPanel.client"
      :agent-id="filesPanel.agentId"
      :agent-name="filesPanel.agentName"
      :agent-emoji="filesPanel.agentEmoji"
      @close="closeFiles"
      class="files-panel-overlay"
    />
    </template>

    <!-- 云电脑支付二维码弹窗 -->
    <el-dialog title="云电脑安装 - 微信支付" :visible.sync="showCloudPayDialog" width="360px" :before-close="closeCloudPayDialog" center :modal-append-to-body="false">
      <div style="text-align:center">
        <p style="font-size:24px;font-weight:bold;color:#e6a23c;margin-bottom:16px">¥ {{ (cloudPayPrice / 100).toFixed(2) }}</p>
        <p style="margin-bottom:12px;color:#666">请使用微信扫码支付</p>
        <img v-if="cloudPayQrUrl" :src="'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(cloudPayQrUrl)" style="width:200px;height:200px" />
        <p style="margin-top:12px;color:#999;font-size:12px">支付完成后将自动跳转</p>
      </div>
    </el-dialog>

  </div>
</template>

<script>
import { listBots, createBot, deleteBot, listDevices, probeGatewayUrl, getOnlineTerminals, listModels } from '@/api/openclawApi.js';
import { GatewayClient, createGatewayClientFromDevice } from '@/api/gatewayClient.js';
import AgentFilesPanel from './AgentFilesPanel.vue';
import AgentOrgTopology from './AgentOrgTopology.vue';

// 预设颜色池（参考 ClawPort 风格）
const AGENT_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#a855f7', '#e11d48',
];
const AGENT_EMOJIS = ['🤖', '🧠', '⚡', '🔧', '📊', '💬', '🎯', '🛡️', '📝', '🔍', '🚀', '🎨'];

export default {
  name: 'AgentDashboard',
  components: { AgentFilesPanel, AgentOrgTopology },
  props: {
    mode: { type: String, default: 'local' } // 'local' | 'cloud'
  },
  data() {
    return {
      bots: [],
      allBots: [],
      loading: false,
      openclawNotInstalled: false,
      installTab: 'windows',
      psCmd: 'irm https://www.shadanai.com/openme-setup.ps1 | iex',
      shCmd: 'curl -fsSL https://www.shadanai.com/openme-setup.sh | bash',
      viewMode: 'list',
      devices: [],
      selectedDeviceIdx: 0,
      gatewayUrl: '',
      gatewayToken: '',
      probing: false,
      showCreateDialog: false,
      creating: false,
      createForm: { agentId: '', nickName: '', headImage: '', model: '' },
      availableModels: [],
      modelsLoading: false,
      // Gateway client for agent management
      gwClient: null,
      // Online bot user IDs
      botOnlineSet: new Set(),
      // Files panel state
      filesPanel: { visible: false, client: null, agentId: '', agentName: '', agentEmoji: '' },
      // 云电脑支付
      showCloudPayDialog: false,
      cloudPayQrUrl: '',
      cloudPayOrderNo: '',
      cloudPayPolling: null,
      cloudPayPrice: 39900,
      cloudPayLoading: false,
      cloudReady: false,
      cloudInstalling: false,
      cloudStatusMsg: '',
    };
  },
  computed: {
    onlineCount() {
      return this.bots.filter(b => this.isBotOnline(b)).length;
    },
  },
  watch: {
    mode() {
      if (this.mode === 'cloud') {
        this.checkCloudStatus();
      } else {
        this.cloudInstalling = false;
        this.loadBots();
      }
    },
    viewMode(v) {
      if (v === 'gateway' && !this.gatewayUrl) this.loadDevices();
    },
    showCreateDialog(v) {
      if (v && !this.availableModels.length) this.loadModels();
    },
  },
  methods: {
    async checkCloudStatus() {
      const userId = this.$store.state.userStore.userInfo?.id;
      try {
        // 1. 检测订单
        const res = await fetch('/wxpay/cloud-pc/status?userId=' + userId).then(r => r.json());
        if (!res.result) {
          this.openclawNotInstalled = true;
          return;
        }

        // 2. 有订单，检测 Pod 和 openme
        this.cloudInstalling = true;
        this.cloudStatusMsg = '正在检测云端环境...';
        this.openclawNotInstalled = false;
        this.pollCloudPcReady();
      } catch {
        this.openclawNotInstalled = true;
      }
    },

    async testGatewayReady(gwUrl) {
      try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 1500);
        const res = await fetch(gwUrl + '/' + Date.now(), { signal: ctrl.signal });
        clearTimeout(timer);
        return res.ok || res.status === 401 || res.status === 403;
      } catch { return false; }
    },

    async checkPodAndOpenme(userId) {
      const gwUrl = `https://18789-${userId}.vnc.shadanai.com`;
      
      // 先测 gateway，如果可用直接返回
      const gwReady = await this.testGatewayReady(gwUrl);
      if (gwReady) {
        return { ready: true, message: '云电脑已就绪' };
      }

      // gateway 不可用，查 Pod 状态
      try {
        const podRes = await fetch(`http://api.vnc.service.thinkgs.cn/api/v1/pods/vnc-${userId}`, {
          headers: { 'Authorization': 'Bearer ' + sessionStorage.getItem('tokenKey') }
        }).then(r => r.json());

        if (podRes.detail) {
          // Pod 不存在或认证失败
          return { ready: false, message: '正在创建云电脑...' };
        }

        const phase = podRes.phase || podRes.status?.phase;
        if (phase === 'Running') {
          return { ready: false, message: '正在安装 openme，预计 1-2 分钟...' };
        } else if (phase === 'Pending') {
          return { ready: false, message: '正在启动 Pod，预计 30-60 秒...' };
        } else {
          return { ready: false, message: '正在创建云电脑...' };
        }
      } catch {
        return { ready: false, message: '正在创建云电脑...' };
      }
    },

    pollCloudPcReady() {
      // 清理之前的轮询
      if (this._gwPoll) clearInterval(this._gwPoll);
      if (this._loadingMsg) { this._loadingMsg.close(); this._loadingMsg = null; }
      
      const userId = this.$store.state.userStore.userInfo?.id;
      const gwUrl = `https://18789-${userId}.vnc.shadanai.com`;
      let attempts = 0;
      const maxAttempts = 60;
      
      this._gwPoll = setInterval(async () => {
        attempts++;
        const status = await this.checkPodAndOpenme(userId);
        
        // 状态直接显示在页面上，不弹 $message
        this.cloudStatusMsg = status.message;
        
        if (status.ready) {
          clearInterval(this._gwPoll);
          this.cloudInstalling = false;
          this.cloudReady = true;
          this.gatewayUrl = gwUrl + '/#token=276f6078de595da4b3402eea9ed9c0e61dbf7612b8f705cc31d65d6104aaef75';
          this.$message.success('云电脑已就绪！');
          this.loadBots();
        } else if (attempts >= maxAttempts) {
          clearInterval(this._gwPoll);
          this.cloudInstalling = false;
          this.openclawNotInstalled = true;
          this.$message.error('云电脑开通超时，请联系客服');
        }
      }, 5000);
    },

    async loadDevices() {
      const userId = this.$store.state.userStore.userInfo.id;
      this.probing = true;
      try {
        this.devices = (await listDevices(userId) || []).sort((a, b) => new Date(b.createdTime || 0) - new Date(a.createdTime || 0));
        if (this.devices.length) {
          this.selectedDeviceIdx = 0;
          const device = this.devices[0];
          this.gatewayUrl = await probeGatewayUrl(device, device.gatewayToken || '');
        } else {
          this.gatewayUrl = '';
        }
      } catch (e) {
        console.warn('[AgentDashboard] 加载设备失败:', e);
        this.gatewayUrl = '';
      } finally {
        this.probing = false;
      }
    },
    async onDeviceChange(idx) {
      this.probing = true;
      const device = this.devices[idx];
      this.gatewayUrl = await probeGatewayUrl(device, device.gatewayToken || '');
      this.probing = false;
    },
    async loadBots() {
      this.loading = true;
      try {
        const userId = this.$store.state.userStore.userInfo.id;
        this.devices = (await listDevices(userId) || []).sort((a, b) => new Date(b.createdTime || 0) - new Date(a.createdTime || 0));
        this.allBots = await listBots();
        // 按设备类型过滤：建立 nodeId -> deviceType 映射
        const deviceTypeMap = {};
        (this.devices || []).forEach(d => { if (d.nodeId) deviceTypeMap[d.nodeId] = d.deviceType || 'local'; });
        const hasCloudDevices = Object.values(deviceTypeMap).some(t => t === 'cloud');
        const hasLocalDevices = Object.values(deviceTypeMap).some(t => t === 'local');
        if (this.mode === 'local' && hasLocalDevices) {
          this.bots = this.allBots.filter(b => !b.nodeId || !deviceTypeMap[b.nodeId] || deviceTypeMap[b.nodeId] === 'local');
        } else if (this.mode === 'cloud' && hasCloudDevices) {
          this.bots = this.allBots.filter(b => !b.nodeId || !deviceTypeMap[b.nodeId] || deviceTypeMap[b.nodeId] === 'cloud');
        } else {
          this.bots = this.allBots;
        }
        if (this.bots && this.bots.length > 0) {
          const botIds = this.bots.map(b => b.id);
          try {
            const terminals = await getOnlineTerminals(botIds);
            this.botOnlineSet = new Set((terminals || []).map(t => t.userId));
          } catch { this.botOnlineSet = new Set(); }
        }
        // 两个端独立判断：本地看本地设备，云端看 cloudReady
        if (this.mode === 'local') {
          this.openclawNotInstalled = !this.devices.some(d => !d.deviceType || d.deviceType === 'local');
        } else {
          this.openclawNotInstalled = !this.cloudReady;
        }
      } catch (e) {
        console.warn('[AgentDashboard] 加载失败:', e);
        if (this.mode !== 'cloud' || !this.cloudReady) this.openclawNotInstalled = true;
      } finally {
        this.loading = false;
      }
    },

    retryLoad() {
      this.openclawNotInstalled = false;
      this.loadBots();
    },

    copyCmd(cmd) {
      navigator.clipboard.writeText(cmd).then(() => {
        this.$message.success('已复制到剪贴板');
      }).catch(() => {
        this.$message.info('请手动复制命令');
      });
    },

    async onCloudInstallTest() {
      const userId = this.$store.state.userStore.userInfo?.id;
      if (!userId) { this.$message.error('请先登录'); return; }
      this.cloudPayLoading = true;
      try {
        const res = await fetch('/wxpay/tokens/purchase', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, packageId: 'cloud-pc-yearly', packageName: '云电脑安装(年)-测试', tokenAmount: 0, price: 1, paymentMethod: 'WECHAT' })
        }).then(r => r.json());
        if (res.code === 200 && res.result) {
          this.cloudPayQrUrl = res.result.qrCodeUrl;
          this.cloudPayOrderNo = res.result.transactionId;
          this.cloudPayPrice = 1;
          this.showCloudPayDialog = true;
          this.startCloudPayPolling();
        } else {
          this.$message.error(res.message || '创建订单失败');
        }
      } catch (e) {
        this.$message.error('支付请求失败: ' + e.message);
      } finally {
        this.cloudPayLoading = false;
      }
    },

    async onCloudInstall() {
      const userId = this.$store.state.userStore.userInfo?.id;
      if (!userId) { this.$message.error('请先登录'); return; }
      this.cloudPayLoading = true;
      try {
        const res = await fetch('/wxpay/tokens/purchase', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, packageId: 'cloud-pc-yearly', packageName: '云电脑安装(年)', tokenAmount: 0, price: 39900, paymentMethod: 'WECHAT' })
        }).then(r => r.json());
        if (res.code === 200 && res.result) {
          this.cloudPayQrUrl = res.result.qrCodeUrl;
          this.cloudPayOrderNo = res.result.transactionId;
          this.cloudPayPrice = 39900;
          this.showCloudPayDialog = true;
          this.startCloudPayPolling();
        } else {
          this.$message.error(res.message || '创建订单失败');
        }
      } catch (e) {
        this.$message.error('支付请求失败: ' + e.message);
      } finally {
        this.cloudPayLoading = false;
      }
    },

    startCloudPayPolling() {
      clearInterval(this.cloudPayPolling);
      this.cloudPayPolling = setInterval(async () => {
        try {
          const res = await fetch('/wxpay/query/' + this.cloudPayOrderNo).then(r => r.json());
          if (res.code === 200 && res.result) {
            let tradeState = res.result;
            try { tradeState = JSON.parse(res.result).trade_state; } catch(e) { console.warn(e); }
            if (tradeState === 'SUCCESS') {
              clearInterval(this.cloudPayPolling);
              this.showCloudPayDialog = false;
              this.$message.success('支付成功！云电脑正在开通中...');
              if (this.mode === 'cloud') {
                this.cloudInstalling = true;
                this.openclawNotInstalled = false;
                this.pollCloudPcReady();
              }
            }
          }
        } catch (e) { console.warn('轮询失败', e); }
      }, 3000);
    },

    closeCloudPayDialog() {
      clearInterval(this.cloudPayPolling);
      this.showCloudPayDialog = false;
    },


    onContactUs() {
      this.$message.info('请联系：service@shadanai.com');
    },

    async loadModels() {
      const tokenKey = sessionStorage.getItem('tokenKey') || this.$store.state.userStore.userInfo?.tokenKey;
      if (!tokenKey) return;
      this.modelsLoading = true;
      try {
        this.availableModels = await listModels(tokenKey);
      } catch (e) {
        console.warn('[AgentDashboard] 加载模型失败:', e);
      } finally {
        this.modelsLoading = false;
      }
    },

    isBotOnline(bot) {
      return this.botOnlineSet.has(bot.id);
    },

    getAgentColor(bot) {
      const idx = Math.abs(this.hashCode(bot.openclawAgentId || bot.nickName)) % AGENT_COLORS.length;
      return AGENT_COLORS[idx];
    },

    getAgentEmoji(bot) {
      const idx = Math.abs(this.hashCode(bot.openclawAgentId || bot.nickName)) % AGENT_EMOJIS.length;
      return AGENT_EMOJIS[idx];
    },

    hashCode(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
      }
      return hash;
    },

    openChat(bot) {
      // bot 直接打开聊天，不需要好友关系
      this._openPrivateChat(bot);
    },

    _openPrivateChat(bot) {
      const chatInfo = {
        type: 'PRIVATE',
        targetId: bot.id,
        showName: bot.nickName,
        headImage: bot.headImage || '',
      };
      this.$store.commit('openChat', chatInfo);
      this.$store.commit('activeChat', this.$store.state.chatStore.chats.findIndex(
        c => c.type === 'PRIVATE' && c.targetId === bot.id
      ));
      this.$emit('chat-opened', chatInfo);
    },

    async onCreate() {
      if (!this.createForm.nickName) {
        this.$message.warning('请填写昵称');
        return;
      }
      this.creating = true;
      // Generate a stable random agent ID
      const agentId = 'bot-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      try {
        // 1. im-platform: create bot IM account
        await createBot({ agentId, nickName: this.createForm.nickName, headImage: this.createForm.headImage });

        // 2. openclaw gateway: create agent workspace (best-effort, same agentId)
        try {
          const gw = await this.ensureGatewayClient();
          if (gw) {
            const gwResult = await gw.request('agents.create', {
              name: agentId,
              workspace: `~/.openclaw/workspace-${agentId}`,
            });
            console.log('[AgentDashboard] Gateway agent created:', gwResult);
          }
        } catch (gwErr) {
          console.warn('[AgentDashboard] Gateway agent create failed:', gwErr);
          this.$message.warning('IM账号已创建，但OpenClaw代理创建失败: ' + gwErr.message);
        }

        this.$message.success('创建成功');
        this.showCreateDialog = false;
        this.createForm = { agentId: '', nickName: '', headImage: '', model: '' };
        await this.loadBots();
      } catch (e) {
        this.$message.error('创建失败');
      } finally {
        this.creating = false;
      }
    },

    async onDelete(bot) {
      try {
        await this.$confirm(`确定删除「${bot.nickName}」？`, '提示', { type: 'warning' });
        const agentId = bot.openclawAgentId;

        // 1. openclaw gateway: delete agent first (while we still have the agentId)
        try {
          const gw = await this.ensureGatewayClient();
          if (gw) {
            await gw.request('agents.delete', { agentId, deleteFiles: false });
          }
        } catch (gwErr) {
          console.warn('[AgentDashboard] Gateway agent delete failed:', gwErr);
          this.$message.warning('OpenClaw代理删除失败: ' + gwErr.message);
        }

        // 2. im-platform: delete bot account
        await deleteBot(agentId);

        this.$message.success('已删除');
        await this.loadBots();
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败');
      }
    },

    /** Lazily connect to openclaw gateway, returns client or null */
    async ensureGatewayClient() {
      if (this.gwClient && this.gwClient.connected) return this.gwClient;
      // 云模式直接用云端 gateway
      if (this.mode === 'cloud' && this.cloudReady) {
        const userId = this.$store.state.userStore.userInfo?.id;
        const url = `wss://18789-${userId}.vnc.shadanai.com`;
        const token = '276f6078de595da4b3402eea9ed9c0e61dbf7612b8f705cc31d65d6104aaef75';
        this.gwClient = new GatewayClient(url, token);
        await this.gwClient.connect();
        return this.gwClient;
      }
      // 本地模式走设备探测
      if (!this.devices.length) {
        const userId = this.$store.state.userStore.userInfo.id;
        this.devices = (await listDevices(userId) || []).sort((a, b) => new Date(b.createdTime || 0) - new Date(a.createdTime || 0));
      }
      if (!this.devices.length) return null;
      try {
        this.gwClient = await createGatewayClientFromDevice(this.devices[this.selectedDeviceIdx] || this.devices[0]);
        return this.gwClient;
      } catch (e) {
        console.warn('[AgentDashboard] Gateway connect failed:', e.message);
        return null;
      }
    },

    async openFiles(bot) {
      try {
        const gw = await this.ensureGatewayClient();
        if (!gw) {
          // Gateway connect failed — likely self-signed cert not trusted.
          // Guide user to accept cert via gateway iframe first.
          if (!this.devices.length) {
            const userId = this.$store.state.userStore.userInfo.id;
            this.devices = (await listDevices(userId) || []).sort((a, b) => new Date(b.createdTime || 0) - new Date(a.createdTime || 0));
          }
          const device = this.devices[this.selectedDeviceIdx] || this.devices[0];
          if (device && device.protocol === 'https' && device.customHost) {
            const host = device.customHost;
            const url = /^https?:\/\//i.test(host || '') ? host : `https://${host}`;
            this.$confirm(
              '需要先信任网关的安全证书。点击确定后会打开网关页面，请在浏览器中接受证书，然后回来重试。',
              '证书信任',
              { confirmButtonText: '打开网关', cancelButtonText: '取消', type: 'warning' }
            ).then(() => {
              window.open(url, '_blank');
            }).catch(() => {});
          } else {
            this.$message.warning('无法连接到 OpenClaw 网关，请确认设备在线');
          }
          return;
        }
        this.filesPanel = {
          visible: true,
          client: gw,
          agentId: bot.openclawAgentId,
          agentName: bot.nickName,
          agentEmoji: this.getAgentEmoji(bot),
        };
      } catch (e) {
        this.$message.error('连接网关失败: ' + e.message);
      }
    },

    closeFiles() {
      this.filesPanel = { visible: false, client: null, agentId: '', agentName: '', agentEmoji: '' };
    },
  },
  mounted() {
    if (this.mode === 'cloud') {
      this.checkCloudStatus();
    } else {
      this.loadBots();
    }
  },
  beforeDestroy() {
    clearInterval(this.cloudPayPolling);
    if (this._gwPoll) clearInterval(this._gwPoll);
    if (this._loadingMsg) this._loadingMsg.close();
    if (this.gwClient) {
      this.gwClient.close();
      this.gwClient = null;
    }
  },
};
</script>

<style lang="scss" scoped>
.agent-dashboard {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f7f8fa;
  color: #1a1a2e;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.cloud-installing-spinner {
  height: 80px;
}

/* 云电脑启动加载 */
.cloud-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
}
.cloud-loading-icon {
  font-size: 56px;
  margin-bottom: 20px;
  animation: cloudBounce 2s ease-in-out infinite;
}
.cloud-loading-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 16px;
}
.cloud-loading-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: #f0f1ff;
  border-radius: 20px;
  color: #6366f1;
  font-size: 14px;
  margin-bottom: 12px;
}
.cloud-loading-status i {
  font-size: 14px;
}
.cloud-loading-hint {
  font-size: 12px;
  color: #9ca3af;
}
@keyframes cloudBounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

/* Hero */
.dashboard-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: linear-gradient(135deg, #fff 0%, #f0f1ff 100%);
  border-bottom: 1px solid #e5e7eb;
}
.hero-left { display: flex; align-items: center; gap: 14px; }
.hero-icon {
  font-size: 24px;
  width: 38px; height: 38px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(99,102,241,0.1);
  border-radius: 10px;
}
.hero-title { font-size: 16px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.02em; }
.hero-tabs { display: flex; gap: 4px; align-items: center; margin-left: 12px; }
.hero-right { display: flex; align-items: center; gap: 12px; }
.hero-stats { display: flex; gap: 16px; }
.stat-item { text-align: center; }
.stat-value { font-size: 18px; font-weight: 700; color: #1a1a2e; line-height: 1; }
.stat-online { color: #16a34a; }
.stat-label { font-size: 11px; color: #9ca3af; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.05em; }
.device-select { width: 180px; }
.tab {
  padding: 5px 12px; font-size: 13px; color: #9ca3af;
  cursor: pointer; border-radius: 6px; transition: all 0.15s;
  display: flex; align-items: center; gap: 6px;
  &:hover { color: #6b7280; background: #f3f4f6; }
  &.active { color: #4f46e5; background: rgba(99,102,241,0.08); font-weight: 500; }
}
.create-btn {
  background: #6366f1 !important; border-color: #6366f1 !important;
  color: #fff !important; border-radius: 6px !important; font-size: 12px !important;
  &:hover { background: #818cf8 !important; border-color: #818cf8 !important; }
}

/* Body */
.dashboard-body { flex: 1; overflow: auto; padding: 20px 24px; }

/* Grid */
.bot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
}

/* Agent Card */
.agent-card {
  position: relative;
  display: flex; flex-direction: column; align-items: center;
  padding: 20px 16px 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  &:hover {
    border-color: rgba(99,102,241,0.4);
    box-shadow: 0 8px 24px rgba(99,102,241,0.1);
    transform: translateY(-2px);
  }
  &--online { border-color: rgba(22,163,74,0.3); }
}

.card-status-dot {
  position: absolute; top: 12px; right: 12px;
  width: 8px; height: 8px; border-radius: 50%;
  &.online { background: #16a34a; box-shadow: 0 0 6px rgba(22,163,74,0.4); }
  &.offline { background: #d1d5db; }
}

.card-avatar {
  width: 56px; height: 56px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 12px; flex-shrink: 0;
}
.card-avatar-img { width: 100%; height: 100%; border-radius: 16px; object-fit: cover; }
.card-avatar-emoji { font-size: 28px; line-height: 1; }

.card-name {
  font-size: 14px; font-weight: 600; color: #1a1a2e; text-align: center;
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.card-agent-id {
  font-size: 11px; color: #9ca3af; margin-top: 3px;
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.card-tags { display: flex; gap: 6px; margin-top: 10px; }
.tag { font-size: 10px; padding: 2px 8px; border-radius: 10px; font-weight: 500; letter-spacing: 0.02em; }
.tag-bot { background: rgba(99,102,241,0.1); color: #6366f1; }
.tag-online { background: rgba(22,163,74,0.1); color: #16a34a; }

.card-actions {
  display: flex; gap: 6px; margin-top: 12px;
  opacity: 0; transition: opacity 0.15s;
}
.agent-card:hover .card-actions { opacity: 1; }

.action-btn {
  width: 30px; height: 30px; border-radius: 8px;
  border: 1px solid #e5e7eb; background: #f9fafb;
  color: #6b7280; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px; transition: all 0.15s;
  &:hover { background: #f3f4f6; color: #1a1a2e; }
}
.action-delete:hover { color: #ef4444; background: rgba(239,68,68,0.06); }

/* Empty */
.empty-state {
  grid-column: 1 / -1;
  display: flex; flex-direction: column; align-items: center; padding: 60px 0;
  .empty-icon { font-size: 48px; margin-bottom: 16px; }
  .empty-title { font-size: 16px; font-weight: 600; color: #1a1a2e; }
  .empty-desc { font-size: 13px; color: #9ca3af; margin: 6px 0 20px; }
}

/* Gateway iframe */
.gateway-body { padding: 0; display: flex; flex-direction: column; }
.gateway-toolbar { padding: 8px 16px; border-bottom: 1px solid #e5e7eb; background: #fff; flex-shrink: 0; }
.gateway-iframe { flex: 1; width: 100%; border: none; background: #f7f8fa; }

/* Org topology */
.org-body { padding: 0; overflow: hidden; }

/* OpenClaw 安装引导 */
.openclaw-setup-guide {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  display: flex; flex-direction: column; align-items: center;
  padding: 40px 32px 48px; text-align: center; overflow: auto;
  background: #f7f8fa;
}
.setup-icon { font-size: 48px; margin-bottom: 12px; }
.setup-title { font-size: 20px; font-weight: 700; color: #1a1a2e; }
.setup-desc { font-size: 13px; color: #9ca3af; margin: 6px 0 24px; white-space: nowrap; }

/* 双卡片布局 */
.setup-cards {
  display: flex; align-items: stretch; justify-content: center; gap: 0; width: 100%; max-width: 780px;
  margin: 0 auto;
}
.setup-card {
  flex: 1; display: flex; flex-direction: column;
  background: #fff; border: 1.5px solid #e5e7eb; border-radius: 14px;
  padding: 20px; position: relative; text-align: left;
  transition: border-color 0.2s;
  &:hover { border-color: #c7c9ce; }
}
.setup-card.card-cloud {
  flex: none; width: 340px;
  border-color: rgba(99,102,241,0.3);
  background: linear-gradient(180deg, rgba(99,102,241,0.03) 0%, #fff 100%);
  &:hover { border-color: rgba(99,102,241,0.5); }
}
.card-header {
  display: flex; align-items: center; gap: 12px; margin-bottom: 14px;
}
.card-icon { font-size: 28px; }
.card-title { font-size: 15px; font-weight: 600; color: #1a1a2e; }
.card-subtitle { font-size: 12px; color: #9ca3af; margin-top: 1px; }
.card-badge {
  position: absolute; top: 14px; right: 14px;
  font-size: 11px; padding: 2px 10px; border-radius: 10px; font-weight: 500;
  &.free { background: #f0fdf4; color: #16a34a; }
  &.recommend { background: rgba(99,102,241,0.1); color: #4f46e5; }
}
.card-body { flex: 1; }
.card-footer { margin-top: 16px; padding-top: 14px; border-top: 1px solid #f3f4f6; }

/* 卡片间分隔 */
.setup-cards-divider {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 0 16px; flex-shrink: 0;
  span {
    font-size: 12px; color: #9ca3af; background: #f7f8fa;
    padding: 6px 0;
  }
}
.cloud-advantages {
  list-style: none; padding: 0; margin: 0;
  li {
    font-size: 13px; color: #374151; padding: 6px 0;
    b { color: #1a1a2e; }
  }
}
.cloud-contact { font-size: 12px; color: #9ca3af; margin-top: 10px;
  a { color: #4f46e5; text-decoration: none; &:hover { text-decoration: underline; } }
}
.platform-tabs {
  display: inline-flex; gap: 0; margin-bottom: 14px;
  border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;
}
.platform-tab {
  padding: 7px 16px; font-size: 13px; color: #6b7280;
  cursor: pointer; user-select: none;
  border-right: 1px solid #e5e7eb;
  &:last-child { border-right: none; }
  &:hover { background: #f9fafb; }
  &.active { color: #4f46e5; background: rgba(99,102,241,0.08); }
}
.install-content { }
.install-hint {
  font-size: 12px; color: #6b7280; margin-bottom: 6px;
  &.install-hint-step1 { height: 32px; }
  kbd {
    display: inline-block; padding: 1px 6px; font-size: 11px;
    background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px;
    font-family: inherit; color: #374151;
  }
}
.install-cmd {
  display: flex; align-items: center; justify-content: space-between;
  background: #1e1e2e; color: #a5f3fc; padding: 10px 14px;
  border-radius: 8px; cursor: pointer; font-size: 13px;
  font-family: 'SF Mono', 'Fira Code', Consolas, monospace;
  transition: background 0.15s;
  &:hover { background: #2a2a3e; }
  code { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .copy-icon { color: #9ca3af; margin-left: 10px; flex-shrink: 0; font-size: 14px; }
}

/* Edit button */
.action-edit {
  color: #6366f1;
  &:hover { background: rgba(99,102,241,0.1); }
}

/* Files panel overlay */
.files-panel-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 10;
  background: #fff;
}
</style>
